import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import PermissionedComponent from '~/components/Common/PermissionedComponent';

import {
  useGetCharterSeatConfigurationOverrideDetail,
  useGetCharterSeatConfigurationOverrideFlights,
  useUpdateCharterSeatConfigurationOverrides,
} from '~/hooks/apis/flights-charter/useCharterSeatConfigurationOverrides';

import type { CharterSeatConfigurationOverrideFlight, CharterSeatConfigurationSeat } from '~/services/CharterService';

import type { CharterCabinClass, SeatInfo, SeatMapRow } from '../charter.types';
import { generateSeatMap } from '../common/generateSeatMap';
import { getFacilityLayoutAttributes } from '../common/getFacilityLayoutAttributes';
import FacilityTile from '../components/FacilityTile';
import { BUSINESS_SEAT_WIDTH, SEAT_HEIGHT } from '../components/seatConstants';

import {
  type BulkSeatOverrideFormValue,
  type BulkSeatOverrideResetValue,
  CABIN_LABELS,
  MANUAL_BLOCK_REASON_LABELS,
  SEAT_OVERRIDE_DEPARTING_ARR_AIRPORT,
  SEAT_OVERRIDE_DEPARTING_DEP_AIRPORT,
  SEAT_OVERRIDE_RETURNING_ARR_AIRPORT,
  SEAT_OVERRIDE_RETURNING_DEP_AIRPORT,
  type SeatOverrideFormValue,
  buildBulkSeatConfigurationOverridePayload,
  buildResetToBaseSeatConfigurationOverridePayload,
  getSeatCanonicalId,
  isSeatSelectableForBulk,
} from './CharterSeatConfigurationOverridesHelpers';
import CharterSeatOverrideDialog from './CharterSeatOverrideDialog';

const FLIGHT_LIST_LIMIT = 1000;
const ADMIN_ECONOMY_SEAT_WIDTH = 88;

const SEAT_COLORS = {
  available: '#80cbc4',
  booked: '#1e88e5',
  priceOverride: '#fff59d',
  baseBlock: '#cfd8dc',
  manualBlock: '#ef9a9a',
} as const;

function formatFlightLabel(flight: CharterSeatConfigurationOverrideFlight): string {
  const flightNumber = flight.flightNumber ? `${flight.flightNumber} ` : '';
  return `${flightNumber}${flight.depAirport} -> ${flight.arrAirport} ${flight.depDate} ${flight.depTime}`;
}

function getSeatStateLookup(seats: Array<CharterSeatConfigurationSeat>): Map<string, CharterSeatConfigurationSeat> {
  return new Map(seats.map((seat) => [`${seat.cabin}:${seat.seatId}`, seat]));
}

function applySeatStatesToRows(
  rows: Array<SeatMapRow>,
  seatStateLookup: Map<string, CharterSeatConfigurationSeat>,
): Array<SeatMapRow> {
  return rows.map((row) => ({
    ...row,
    seats: row.seats.map((seat) => {
      const seatState = seatStateLookup.get(`${row.cabin}:${seat.primaryColumn ?? seat.seatId}`);
      if (!seatState) {
        return seat;
      }

      return {
        ...seat,
        price: seatState.price,
        basePrice: seatState.basePrice,
        isAvailable: seatState.isAvailable,
        baseIsAvailable: seatState.baseIsAvailable,
        blockReason: seatState.blockReason,
        baseBlockReason: seatState.baseBlockReason,
        displayLabel: seatState.displayLabel ?? seat.displayLabel,
        isOccupied: seatState.isOccupied,
        hasPriceOverride: seatState.hasPriceOverride,
        hasAvailabilityOverride: seatState.hasAvailabilityOverride,
        hasBlockReasonOverride: seatState.hasBlockReasonOverride,
        isOverride: seatState.isOverride,
      };
    }),
  }));
}

function SeatLegend(): React.JSX.Element {
  const items = [
    { label: 'Available', color: SEAT_COLORS.available },
    { label: 'Booked', color: SEAT_COLORS.booked, textColor: 'common.white' },
    { label: 'Price override', color: SEAT_COLORS.priceOverride },
    { label: 'Base block', color: SEAT_COLORS.baseBlock },
    { label: 'Manual block', color: SEAT_COLORS.manualBlock },
  ];

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {items.map((item) => (
          <Chip
            key={item.label}
            label={item.label}
            sx={{ bgcolor: item.color, color: item.textColor ?? 'text.primary', fontWeight: 500 }}
          />
        ))}
      </Stack>
    </Paper>
  );
}

function getSeatColor({ seat }: { seat: SeatInfo }): string {
  if (seat.isOccupied) {
    return SEAT_COLORS.booked;
  }
  if (!seat.isAvailable && seat.isOverride) {
    return SEAT_COLORS.manualBlock;
  }
  if (!seat.isAvailable) {
    return SEAT_COLORS.baseBlock;
  }
  if (seat.hasPriceOverride) {
    return SEAT_COLORS.priceOverride;
  }
  return SEAT_COLORS.available;
}

function AdminSeatTile({
  seat,
  isSelected,
  onClick,
  columnSpan = 1,
}: {
  seat: SeatInfo;
  isSelected: boolean;
  onClick: (seat: SeatInfo) => void;
  columnSpan?: number;
}): React.JSX.Element {
  const label = seat.displayLabel ?? seat.seatId;
  const seatWidth =
    (seat.cabin === 'business' ? BUSINESS_SEAT_WIDTH : ADMIN_ECONOMY_SEAT_WIDTH) * Math.max(1, columnSpan);
  const isSelectable = isSeatSelectableForBulk(seat);
  const blockReason =
    seat.blockReason && seat.blockReason !== 'occupied' ? MANUAL_BLOCK_REASON_LABELS[seat.blockReason] : null;
  const seatStatusLabel = seat.isOccupied ? 'Booked' : !seat.isAvailable ? (blockReason ?? 'Blocked') : null;
  const title = [
    `Seat ${label}`,
    seat.isOccupied ? 'Booked' : seat.isAvailable ? 'Available' : 'Blocked',
    blockReason,
    `Price $${seat.price ?? 0}`,
    seat.hasPriceOverride ? `Base $${seat.basePrice ?? 0}` : null,
  ]
    .filter(Boolean)
    .join(' - ');

  return (
    <Tooltip title={title}>
      <Box
        component="button"
        type="button"
        onClick={() => onClick(seat)}
        sx={{
          bgcolor: getSeatColor({ seat }),
          border: isSelected ? '2px solid' : '1px solid',
          borderColor: isSelected ? 'primary.main' : 'divider',
          borderRadius: 1,
          boxShadow: isSelected ? 2 : 'none',
          color: seat.isOccupied ? 'common.white' : 'text.primary',
          width: seatWidth,
          height: SEAT_HEIGHT,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 0.25,
          py: 0.5,
          textAlign: 'center',
          cursor: 'pointer',
          position: 'relative',
          gridColumn: columnSpan > 1 ? `span ${columnSpan}` : undefined,
          '&:hover': {
            filter: 'brightness(0.96)',
          },
        }}
      >
        {isSelectable ? (
          <Checkbox
            checked={isSelected}
            size="small"
            tabIndex={-1}
            disableRipple
            inputProps={{ 'aria-label': isSelected ? `Deselect seat ${label}` : `Select seat ${label}` }}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              width: 20,
              height: 20,
              p: 0,
              color: 'text.secondary',
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: isSelected ? 'primary.main' : 'divider',
              borderRadius: 0.75,
              '&.Mui-checked': {
                color: 'primary.main',
              },
              '& .MuiSvgIcon-root': {
                fontSize: 18,
              },
            }}
          />
        ) : null}
        <Typography variant="caption" sx={{ fontSize: 12, lineHeight: 1.1, fontWeight: 600 }}>
          ${seat.price ?? 0}
        </Typography>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: 16, lineHeight: 1.1 }}>
          {label}
        </Typography>
        {seatStatusLabel ? (
          <Typography
            variant="caption"
            sx={{
              fontSize: 10,
              lineHeight: 1,
              fontWeight: 600,
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {seatStatusLabel}
          </Typography>
        ) : null}
      </Box>
    </Tooltip>
  );
}

function AdminSeatRow({
  row,
  selectedSeatIds,
  onSeatClick,
}: {
  row: SeatMapRow;
  selectedSeatIds: ReadonlySet<string>;
  onSeatClick: (seat: SeatInfo) => void;
}): React.JSX.Element {
  const seatWidth = row.cabin === 'business' ? BUSINESS_SEAT_WIDTH : ADMIN_ECONOMY_SEAT_WIDTH;
  const aisleWidth = row.cabin === 'business' ? 18 : 12;
  const facilityLayoutAttributes = new Map<number, ReturnType<typeof getFacilityLayoutAttributes>>();
  const gridTemplateColumns = row.layout
    .flatMap((segment, index) => {
      if (segment.kind === 'aisle') {
        return [`${aisleWidth}px`];
      }
      if (segment.kind === 'seat_group') {
        return Array.from(
          { length: Math.max(1, segment.spanUnits ?? segment.columns?.length ?? 1) },
          () => `${seatWidth}px`,
        );
      }
      if (segment.kind === 'facility') {
        const attributes = getFacilityLayoutAttributes(segment);
        facilityLayoutAttributes.set(index, attributes);
        return Array.from({ length: attributes.columnSpan }, () => `${seatWidth}px`);
      }
      return [`${seatWidth}px`];
    })
    .join(' ');

  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
      <Typography variant="caption" sx={{ width: 28, textAlign: 'right', fontWeight: 600, flexShrink: 0 }}>
        {row.isFacilityRow ? '' : row.rowNumber}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns,
          gap: 0.75,
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
          px: 1.5,
        }}
      >
        {row.layout.map((segment, index) => {
          if (segment.kind === 'aisle') {
            return <Box key={`${row.rowId}-aisle-${index}`} sx={{ width: '100%', height: SEAT_HEIGHT }} />;
          }

          if (segment.kind === 'facility') {
            return (
              <FacilityTile
                key={`${row.rowId}-facility-${index}`}
                facility={segment}
                attributes={facilityLayoutAttributes.get(index)}
              />
            );
          }

          if (segment.kind === 'placeholder') {
            return <Box key={`${row.rowId}-placeholder-${index}`} sx={{ width: '100%', height: SEAT_HEIGHT }} />;
          }

          const primaryCode = segment.kind === 'seat_group' ? segment.primaryCode : segment.code;
          const seatId = `${row.rowNumber}${primaryCode}`;
          const seat = row.seats.find((item) => item.seatId === seatId);
          if (!seat) {
            return <Box key={`${row.rowId}-${primaryCode}-${index}`} sx={{ width: '100%', height: SEAT_HEIGHT }} />;
          }

          const columnSpan =
            segment.kind === 'seat_group' ? Math.max(1, segment.spanUnits ?? segment.columns?.length ?? 1) : 1;
          return (
            <AdminSeatTile
              key={`${row.rowId}-${primaryCode}-${index}`}
              seat={seat}
              isSelected={selectedSeatIds.has(getSeatCanonicalId(seat))}
              onClick={onSeatClick}
              columnSpan={columnSpan}
            />
          );
        })}
      </Box>
    </Stack>
  );
}

export default function CharterSeatConfigurationOverridesPage(): React.JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedFlightId, setSelectedFlightId] = useState<string | null>(null);
  const [selectedCabin, setSelectedCabin] = useState<CharterCabinClass | null>(null);
  const [selectedSeatIds, setSelectedSeatIds] = useState<ReadonlySet<string>>(() => new Set());
  const [dialogSeats, setDialogSeats] = useState<Array<SeatInfo>>([]);

  const queryParams = useMemo(
    () => ({
      departingDepAirport: SEAT_OVERRIDE_DEPARTING_DEP_AIRPORT,
      departingArrAirport: SEAT_OVERRIDE_DEPARTING_ARR_AIRPORT,
      returningDepAirport: SEAT_OVERRIDE_RETURNING_DEP_AIRPORT,
      returningArrAirport: SEAT_OVERRIDE_RETURNING_ARR_AIRPORT,
      offset: 0,
      limit: FLIGHT_LIST_LIMIT,
    }),
    [],
  );

  const flightsQuery = useGetCharterSeatConfigurationOverrideFlights(queryParams);
  const flights = useMemo(() => flightsQuery.data?.result ?? [], [flightsQuery.data?.result]);
  const detailQuery = useGetCharterSeatConfigurationOverrideDetail(selectedFlightId);
  const updateMutation = useUpdateCharterSeatConfigurationOverrides(selectedFlightId);
  const detail = detailQuery.data?.result ?? null;

  useEffect(() => {
    if (!selectedFlightId && flights.length > 0) {
      setSelectedFlightId(flights[0].flightInstanceId);
    }
  }, [flights, selectedFlightId]);

  useEffect(() => {
    if (detail?.cabins.length && (!selectedCabin || !detail.cabins.includes(selectedCabin))) {
      setSelectedCabin(detail.cabins[0]);
    }
  }, [detail, selectedCabin]);

  const seatMaps = useMemo(() => {
    if (!detail) {
      return null;
    }
    const baseRows = generateSeatMap(detail.seatConfiguration);
    return applySeatStatesToRows(baseRows, getSeatStateLookup(detail.seats));
  }, [detail]);

  const cabinRows = useMemo(() => {
    if (!seatMaps || !selectedCabin) {
      return [];
    }
    return seatMaps.filter((row) => row.cabin === selectedCabin);
  }, [seatMaps, selectedCabin]);

  const selectedSeats = useMemo(() => {
    if (!selectedSeatIds.size) {
      return [];
    }

    return cabinRows.flatMap((row) => row.seats).filter((seat) => selectedSeatIds.has(getSeatCanonicalId(seat)));
  }, [cabinRows, selectedSeatIds]);

  const selectedBaseSeat = useMemo(() => {
    const selectedSeat = dialogSeats.length === 1 ? dialogSeats[0] : null;
    if (!selectedSeat) {
      return null;
    }
    return {
      ...selectedSeat,
      price: selectedSeat.basePrice ?? selectedSeat.price,
      isAvailable: selectedSeat.baseIsAvailable ?? selectedSeat.isAvailable,
      blockReason: selectedSeat.baseBlockReason,
    };
  }, [dialogSeats]);

  const clearSeatSelection = useCallback(() => {
    setSelectedSeatIds(new Set());
    setDialogSeats([]);
  }, []);

  const handleSeatClick = useCallback((seat: SeatInfo) => {
    if (!isSeatSelectableForBulk(seat)) {
      setDialogSeats([seat]);
      return;
    }

    const seatId = getSeatCanonicalId(seat);
    setSelectedSeatIds((currentIds) => {
      const nextIds = new Set(currentIds);
      if (nextIds.has(seatId)) {
        nextIds.delete(seatId);
      } else {
        nextIds.add(seatId);
      }
      return nextIds;
    });
  }, []);

  const handleSaveSeat = useCallback(
    async (value: SeatOverrideFormValue | BulkSeatOverrideFormValue | BulkSeatOverrideResetValue) => {
      if (!dialogSeats.length) {
        return;
      }

      try {
        if ('resetToBase' in value) {
          await updateMutation.mutateAsync(buildResetToBaseSeatConfigurationOverridePayload(dialogSeats));
          clearSeatSelection();
          enqueueSnackbar('Seats reset to base.', { variant: 'success' });
          return;
        }

        if ('applyAvailability' in value) {
          await updateMutation.mutateAsync(buildBulkSeatConfigurationOverridePayload(dialogSeats, value));
          clearSeatSelection();
          enqueueSnackbar('Seats saved.', { variant: 'success' });
          return;
        }

        const selectedSeat = dialogSeats[0];
        await updateMutation.mutateAsync(
          buildBulkSeatConfigurationOverridePayload([selectedSeat], {
            applyAvailability: true,
            isAvailable: selectedSeat.isOccupied ? true : value.isAvailable,
            blockReason: selectedSeat.isOccupied ? undefined : value.blockReason,
            applyPrice: true,
            price: selectedSeat.isOccupied ? (selectedSeat.price ?? 0) : value.price,
          }),
        );
        clearSeatSelection();
        enqueueSnackbar('Seat saved.', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar(error instanceof Error ? error.message : 'Unable to save seat.', { variant: 'error' });
      }
    },
    [clearSeatSelection, dialogSeats, enqueueSnackbar, updateMutation],
  );

  return (
    <PermissionedComponent
      fallback={<Alert severity="error">You need the admin-user role to manage charter seat overrides.</Alert>}
    >
      <Container disableGutters>
        <Stack spacing={3}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <FormControl sx={{ minWidth: 420 }} disabled={flightsQuery.isLoading || flights.length === 0}>
              <InputLabel id="charter-seat-override-flight-label">Flight</InputLabel>
              <Select
                labelId="charter-seat-override-flight-label"
                label="Flight"
                value={selectedFlightId ?? ''}
                onChange={(event) => {
                  setSelectedFlightId(event.target.value);
                  setSelectedCabin(null);
                  setSelectedSeatIds(new Set());
                  setDialogSeats([]);
                }}
              >
                {flights.map((flight) => (
                  <MenuItem key={flight.flightInstanceId} value={flight.flightInstanceId}>
                    {formatFlightLabel(flight)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {detail ? (
              <ToggleButtonGroup
                exclusive
                value={selectedCabin}
                onChange={(_, value) => {
                  if (value) {
                    setSelectedCabin(value);
                    setSelectedSeatIds(new Set());
                    setDialogSeats([]);
                  }
                }}
                size="small"
              >
                {detail.cabins.map((cabin) => (
                  <ToggleButton key={cabin} value={cabin}>
                    {CABIN_LABELS[cabin]}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            ) : null}
          </Stack>

          {flightsQuery.isError ? <Alert severity="error">Unable to load charter flights.</Alert> : null}
          {detailQuery.isError ? <Alert severity="error">Unable to load seats for this flight.</Alert> : null}
          {!flightsQuery.isLoading && !flightsQuery.isError && flights.length === 0 ? (
            <Alert severity="info">No open-for-sale Maldives charter flights are available for seat management.</Alert>
          ) : null}

          <SeatLegend />

          {flightsQuery.isLoading || detailQuery.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : null}

          {detail && selectedCabin ? (
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, overflowX: 'auto' }}>
              <Stack spacing={2} alignItems="center" sx={{ minWidth: 720 }}>
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: '100%' }}
                >
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      {CABIN_LABELS[selectedCabin]}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {detail.depAirport} -&gt; {detail.arrAirport} {detail.depDate} {detail.depTime}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      {selectedSeats.length} selected
                    </Typography>
                    <Button
                      variant="text"
                      onClick={() => setSelectedSeatIds(new Set())}
                      disabled={!selectedSeats.length}
                    >
                      Clear selection
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => setDialogSeats(selectedSeats)}
                      disabled={!selectedSeats.length}
                    >
                      Edit selected
                    </Button>
                  </Stack>
                </Stack>

                {cabinRows.map((row) => (
                  <AdminSeatRow
                    key={row.rowId}
                    row={row}
                    selectedSeatIds={selectedSeatIds}
                    onSeatClick={handleSeatClick}
                  />
                ))}
              </Stack>
            </Paper>
          ) : null}
        </Stack>

        <CharterSeatOverrideDialog
          open={dialogSeats.length > 0}
          seats={dialogSeats}
          baseSeat={selectedBaseSeat}
          onClose={() => setDialogSeats([])}
          onSave={handleSaveSeat}
          isSaving={updateMutation.isPending}
        />
      </Container>
    </PermissionedComponent>
  );
}
